import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useConsent } from "@/context/ConsentContext";

const Preferences = () => {
  const { consent, setConsent, prefsOpen, setPrefsOpen } = useConsent();
  const [analytics, setAnalytics] = useState(consent === "accepted");

  useEffect(() => {
    setAnalytics(consent === "accepted");
  }, [consent, prefsOpen]);

  return (
    <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
      <DialogContent className="max-w-lg rounded-none bg-[#FDFBF7]" data-testid="cookie-preferences-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light">Cookie preferences</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-[#5A626A]">
            Choose which cookies and local storage this site may use. You can change this at any time from the footer.
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
          <div className="flex items-start justify-between gap-6 py-5">
            <div>
              <p className="text-sm font-medium">Strictly necessary</p>
              <p className="mt-1 text-xs leading-relaxed text-[#5A626A]">Your bag, secure checkout with Square and admin sign-in. Always on.</p>
            </div>
            <Switch checked disabled data-testid="cookie-essential-switch" />
          </div>
          <div className="flex items-start justify-between gap-6 py-5">
            <div>
              <p className="text-sm font-medium">Analytics</p>
              <p className="mt-1 text-xs leading-relaxed text-[#5A626A]">Anonymous page views and referrers that help the studio understand visits.</p>
            </div>
            <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-analytics-switch" />
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button className="btn-outline px-6 py-3" data-testid="cookie-prefs-reject-btn" onClick={() => setConsent("rejected")}>Reject non-essential</button>
          <button className="btn-primary px-6 py-3" data-testid="cookie-prefs-save-btn" onClick={() => setConsent(analytics ? "accepted" : "rejected")}>Save preferences</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CookieBanner = () => {
  const { consent, setConsent, openPreferences } = useConsent();
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <>
      {!consent && (
        <div data-testid="cookie-banner" className="fade-up fixed inset-x-0 bottom-0 z-50 border-t border-[#E5E0D8] bg-[#FDFBF7]/95 backdrop-blur-md">
          <div className="container-x flex flex-col gap-5 py-6 lg:flex-row lg:items-center lg:justify-between">
            <p className="max-w-2xl text-[13px] leading-relaxed text-[#3A3733]">
              This site uses essential storage for your bag and secure checkout. With your permission, we'd also like to record anonymous visit statistics.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="pill border-transparent text-[#5A626A] hover:text-[#1A1A1A]" data-testid="cookie-preferences-btn" onClick={openPreferences}>Preferences</button>
              <button className="pill border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7]" data-testid="cookie-reject-btn" onClick={() => setConsent("rejected")}>Reject non-essential</button>
              <button className="pill border-[#1A1A1A] bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#3A3733]" data-testid="cookie-accept-all-btn" onClick={() => setConsent("accepted")}>Accept all</button>
            </div>
          </div>
        </div>
      )}
      <Preferences />
    </>
  );
};
